import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import TaskSubtaskRow from './TaskSubtaskRow';
import useEscapeFirst from './useEscapeFirst';
import {
  SUBTASK_MAX_COUNT,
  SUBTASK_TITLE_MAX,
  addSubtask,
  canAddSubtask,
  cleanSubtaskTitle,
  moveSubtask,
  moveSubtaskBy,
  removeSubtask,
  renameSubtask,
  subtaskCounts,
  subtasksOf,
  toggleSubtask,
} from '../../extensions/bundled/task-manager/subtasks';
import './TaskSubtasks.css';

const FULL_COUNT = `A task can hold up to ${SUBTASK_MAX_COUNT} subtasks. Finish or delete one to add more.`;
const FULL_BYTES = 'This checklist is as long as a task can sync. Shorten a few titles or delete one to add more.';
const LONG_TITLE = `Subtask titles are at most ${SUBTASK_TITLE_MAX} characters.`;

/**
 * The task window's checklist (Boards Redesign EXTRAS §1.6 / §2.1): plain
 * todos — a title and done / not done, nothing else (owner, 12.10.2026).
 * Header "Subtasks 2/5" with a thin bar, the items, and an "Add subtask"
 * field that keeps focus after Enter so a list can be typed in one go
 * (Linear, Todoist, Trello). Every edit is one `onChange({ subtasks })` —
 * the board's handleChangeTask, so sync, undo and realtime carry it.
 * The rules (limits, empty title deletes, moves) live in subtasks.js.
 */
export default function TaskSubtasks({ task, onChange, readOnly = false }) {
  const list = useMemo(() => subtasksOf(task), [task]);
  const counts = subtaskCounts(list);
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState(null);
  const rootRef = useRef(null);
  const addRef = useRef(null);
  const refocusRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const atCount = list.length >= SUBTASK_MAX_COUNT;
  const limitText = atCount ? FULL_COUNT : notice;

  const commit = (next) => {
    if (next === list) return false;
    onChange({ subtasks: next });
    return true;
  };

  // An Alt+↑/↓ move re-orders the DOM; put focus back where it was.
  useEffect(() => {
    const want = refocusRef.current;
    if (!want) return;
    refocusRef.current = null;
    const id = String(want.id).replace(/["\\]/g, '\\$&');
    const el = rootRef.current?.querySelector(`[data-subtask-id="${id}"] [data-part="${want.part}"]`);
    if (el && document.activeElement !== el) el.focus();
  }, [list]);

  const add = () => {
    const title = draft.trim();
    if (!title) return;
    if (!canAddSubtask(list, title)) {
      setNotice(list.length >= SUBTASK_MAX_COUNT ? FULL_COUNT : FULL_BYTES);
      return;
    }
    if (commit(addSubtask(list, title))) {
      setDraft('');
      setNotice(null);
    }
    addRef.current?.focus();
  };

  const onRename = (id, title) => {
    const next = renameSubtask(list, id, title);
    // Unchanged once trimmed ("Foo " for "Foo") is not a refusal.
    const clean = cleanSubtaskTitle(title);
    const same = list.some((s) => s.id === id && s.title === clean);
    if (next === list && clean && !same) setNotice(FULL_BYTES);
    else setNotice(null);
    commit(next);
  };

  const onMoveBy = (id, delta, target) => {
    const part = target?.closest?.('[data-part]')?.getAttribute('data-part') || 'title';
    if (commit(moveSubtaskBy(list, id, delta))) refocusRef.current = { id, part };
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const id = String(active.id).replace(/^subtask:/, '');
    const to = list.findIndex((s) => `subtask:${s.id}` === over.id);
    if (to !== -1) commit(moveSubtask(list, id, to));
  };

  // Escape with something typed clears it; an empty field lets Escape close
  // the window as usual.
  useEscapeFirst(!readOnly && draft !== '', () => {
    setDraft('');
    setNotice(null);
  });

  // Nothing to show a viewer of an empty checklist.
  if (readOnly && list.length === 0) return null;

  const pct = counts.total ? Math.round((counts.done / counts.total) * 100) : 0;
  return (
    <section className="task-modal-section task-subtasks" ref={rootRef} aria-label="Subtasks">
      <div className="task-subtasks-head">
        <span className="task-modal-label">
          Subtasks
          {counts.total > 0 && (
            <span className="task-subtasks-count" data-subtasks={`${counts.done}/${counts.total}`}>
              {counts.done}/{counts.total}
            </span>
          )}
        </span>
        {counts.total > 0 && (
          <span
            className="task-subtasks-bar"
            role="progressbar"
            aria-label="Subtasks done"
            aria-valuemin={0}
            aria-valuemax={counts.total}
            aria-valuenow={counts.done}
          >
            <span style={{ width: `${pct}%` }} />
          </span>
        )}
      </div>

      {list.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext items={list.map((s) => `subtask:${s.id}`)} strategy={verticalListSortingStrategy}>
            <ul className="task-subtasks-list">
              {list.map((s) => (
                <TaskSubtaskRow
                  key={s.id}
                  item={s}
                  readOnly={readOnly}
                  onToggle={(id, done) => commit(toggleSubtask(list, id, done))}
                  onRename={onRename}
                  onDelete={(id) => {
                    setNotice(null);
                    commit(removeSubtask(list, id));
                  }}
                  onMoveBy={onMoveBy}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {!readOnly && (
        <div className={`task-subtasks-add${atCount ? ' task-subtasks-add--full' : ''}`}>
          <Plus size={14} aria-hidden className="task-subtasks-add-icon" />
          <input
            ref={addRef}
            type="text"
            className="modal-input task-subtasks-input"
            aria-label="Add subtask"
            placeholder={atCount ? 'Subtask limit reached' : 'Add subtask'}
            value={draft}
            disabled={atCount}
            maxLength={SUBTASK_TITLE_MAX}
            onChange={(e) => {
              setDraft(e.target.value);
              setNotice(e.target.value.length >= SUBTASK_TITLE_MAX ? LONG_TITLE : null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                add();
              }
            }}
          />
        </div>
      )}
      {limitText && !readOnly && (
        <p className="task-subtasks-limit" role="status">
          {limitText}
        </p>
      )}
    </section>
  );
}
